import { ActivityIndicator, View, Pressable, Modal, Platform } from "react-native"
import MCIcon from "@react-native-vector-icons/material-design-icons"
import { SafeAreaView } from "react-native-safe-area-context"
import { Control, useController } from "react-hook-form"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { newAddressInput } from "@white-labels/input"
import { z } from "@white-labels/input"

import { Text, TextInput, Button, KeyboardAwareScrollView, ScrollView, Header } from "@src/components"
import { sharedStyles, trpc } from "@src/utils"
import { ar } from "@src/i18n"

function extractLatLngFromUrl(url: string): { lat: number; lng: number } | null {
  try {
    const regex = /q=(-?\d+(\.\d+)?),(-?\d+(\.\d+)?)/
    const match = url.match(regex)
    if (match) {
      return { lat: parseFloat(match[1]), lng: parseFloat(match[3]) }
    }
    return null
  } catch (_) {
    return null
  }
}

function LocationUrlInput({ control }: { control: Control<z.infer<typeof formSchema>> }) {
  const { field } = useController({ control, name: "locationUrl" })
  const latField = useController({ control, name: "lat" })
  const lngField = useController({ control, name: "lng" })

  const handleTextChange = (text: string) => {
    field.onChange(text)
    const coords = extractLatLngFromUrl(text)
    if (coords) {
      latField.field.onChange(coords.lat)
      lngField.field.onChange(coords.lng)
    }
  }

  return <TextInput value={field.value} onChangeText={handleTextChange} placeholder="https://maps.google.com/?q=..." />
}

export type CustomerAddressFormModalState = null | { type: "add" } | { type: "edit"; addressId: number }

const formSchema = z.object({
  label: z.string().check(z.minLength(1)),
  areaId: z.number(),
  lat: z.optional(z.number()),
  lng: z.optional(z.number()),
  details: z.optional(z.string()),
  building: z.optional(z.string()),
  floor: z.optional(z.string()),
  flat: z.optional(z.string()),
  instructions: z.optional(z.string()),
  locationUrl: z.optional(z.string()),
})

export function CustomerAddressForm({ customerId, addressId, onClose }: { customerId: number; addressId?: number; onClose: () => void }) {
  const utils = trpc.useUtils()
  const deliveryAreas = trpc.deliveryAreas.list.useQuery()
  const createAddress = trpc.addresses.new.useMutation()
  const editAddress = trpc.addresses.edit.useMutation()
  const address = trpc.addresses.list.useQuery({ customerId })

  const existingAddress = addressId ? address.data?.find((a) => a.id === addressId) : undefined

  const { control, formState, handleSubmit } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: existingAddress
      ? {
          label: existingAddress.label,
          areaId: existingAddress.areaId,
          lat: existingAddress.lat ?? undefined,
          lng: existingAddress.lng ?? undefined,
          locationUrl: existingAddress.lat && existingAddress.lng ? `https://maps.google.com/?q=${existingAddress.lat},${existingAddress.lng}` : undefined,
          details: existingAddress.details ?? undefined,
          building: existingAddress.building ?? undefined,
          floor: existingAddress.floor ?? undefined,
          flat: existingAddress.flat ?? undefined,
          instructions: existingAddress.instructions ?? undefined,
        }
      : undefined,
  })

  const { isValid } = formState
  const isPending = createAddress.isPending || editAddress.isPending

  const onSubmit = handleSubmit((data) => {
    if (addressId) {
      editAddress.mutate(
        { ...data, id: addressId as number },
        {
          onSuccess: () => {
            utils.addresses.list.refetch()
            onClose()
          },
        },
      )
    } else {
      const addressData = data as z.infer<typeof newAddressInput>
      createAddress.mutate(
        { ...addressData, customerId },
        {
          onSuccess: () => {
            utils.addresses.list.refetch()
            onClose()
          },
        },
      )
    }
  })

  return (
    <Modal visible animationType="slide" presentationStyle="formSheet" onRequestClose={onClose}>
      <Header
        title={addressId ? ar.editAddress : ar.addAddress}
        withoutInsets={Platform.OS === "ios"}
        canGoBack={false}
        right={
          <Pressable onPress={onClose} className="h-full px-2 justify-center">
            <MCIcon name="close" size={24} />
          </Pressable>
        }
      />

      {deliveryAreas.isLoading ? (
        <ActivityIndicator className="mt-4" />
      ) : (
        <KeyboardAwareScrollView contentContainerStyle={sharedStyles.contentContainer}>
          <View className="mb-4 mt-4">
            <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.addressLabel}*</Text>
            <Controller control={control} name="label" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} placeholder="المنزل، العمل..." />} />
          </View>

          <View className="mb-4">
            <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.locationUrl} (اختياري)</Text>
            <LocationUrlInput control={control} />
          </View>

          <View className="mb-4">
            <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.area}*</Text>
            <Controller
              control={control}
              name="areaId"
              render={({ field }) => (
                <View className="border border-gray-200 rounded bg-white">
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} className="p-2">
                    {deliveryAreas.data?.map((area) => (
                      <Pressable
                        key={area.id}
                        onPress={() => field.onChange(area.id)}
                        className={`px-4 py-2 mx-1 rounded ${field.value === area.id ? "bg-blue-600" : "bg-gray-100"}`}
                      >
                        <Text className={`font-bold ${field.value === area.id ? "text-white" : "text-gray-700"}`}>{area.name}</Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                </View>
              )}
            />
          </View>

          <View className="mb-4">
            <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.addressDetails}</Text>
            <Controller control={control} name="details" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} />} />
          </View>

          <View className="flex-row gap-2 mb-4">
            <View className="flex-1">
              <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.building}</Text>
              <Controller control={control} name="building" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} />} />
            </View>
            <View className="flex-1">
              <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.floor}</Text>
              <Controller control={control} name="floor" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} />} />
            </View>
            <View className="flex-1">
              <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.flat}</Text>
              <Controller control={control} name="flat" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} />} />
            </View>
          </View>

          <View className="mb-6">
            <Text className="text-xs font-bold text-gray-400 mb-2 ml-1 uppercase tracking-wider">{ar.addressInstructions}</Text>
            <Controller control={control} name="instructions" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} />} />
          </View>

          <Button className="bg-blue-600 rounded py-4 shadow-blue-200 shadow-lg active:shadow-none mb-3" loading={isPending} disabled={!isValid} onPress={onSubmit}>
            <Text className="text-white font-bold text-lg">{ar.saveAddress}</Text>
          </Button>

          <SafeAreaView edges={["bottom"]} />
        </KeyboardAwareScrollView>
      )}
    </Modal>
  )
}
